import React from 'react';

import { MOBILE_MAX_WIDTH_LAYOUT } from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

// This's a temporal styling
const sectionStyle = {
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '100px',
  border: '2px solid',
  borderColor: '#ffff',
};

const SCROLL_MARGIN_WITH_SEPARATOR = '52px'; // 40px of spacing + 12px above the title
const SCROLL_MARGIN_WITHOUT_SEPARATOR = '12px'; // Just 12px above the title

export const PageLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex flexDirection="column" alignItems="center" width="100%">
      <Box width="100%" maxWidth={[MOBILE_MAX_WIDTH_LAYOUT, 'initial', 'initial']}>
        {props.children}
      </Box>
    </Flex>
  );
};

export const HeaderLayout = React.forwardRef(
  (props: { children: React.ReactNode }, ref: React.ForwardedRef<HTMLElement>): JSX.Element => {
    return (
      // A Box with display="flex" is used instead of a Flex because the latter cannot accept a ref.
      <Box display="flex" flexDirection={['column', 'column', 'row']} width="100%" ref={ref}>
        {props.children}
      </Box>
    );
  }
);

export const ProfileLayout = (props: { children?: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      sx={{ order: [2, 2, 1] }}
      width={['100%', '100%', '352px']}
      position="relative"
      top={['-24px', '-56px', '0']}
      pb={['4x', '6x', '0x']}
      zIndex="999"
    >
      {props.children}
    </Flex>
  );
};

export const GalleryLayout = (props: {
  isInSitterCropFix: boolean;
  children: React.ReactNode;
}): JSX.Element => {
  return (
    <Flex
      sx={{ order: [1, 1, 2] }}
      pl={['0x', '0x', '12x']}
      width={['100%', '100%', 'initial']}
      flex="1"
      height={props.isInSitterCropFix ? ['378px', '378px', '378px'] : ['378px', 'unset', 'unset']}
    >
      {props.children}
    </Flex>
  );
};

export const PricingAvailabilityLayout = (props: { children: React.ReactNode }): JSX.Element => {
  const theme = useTheme();
  return (
    <Flex
      sx={{ boxShadow: ['none', 'none', '0px 1px 4px 0px rgba(27, 31, 35, 0.32)'] }}
      flexDirection="column"
      borderRadius="8px"
      paddingX={['0x', '0x', '4x']}
      paddingY={['0x', '0x', '4x']}
      border={['none', 'none', `1px solid ${theme.colors.separator.primary}`]}
      background="primary"
      width="100%"
    >
      {props.children}
    </Flex>
  );
};

export const ServiceLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      {...sectionStyle}
      width={['100%', '50%', '100%']}
      id="services"
      sx={{ scrollMarginTop: SCROLL_MARGIN_WITH_SEPARATOR }}
    >
      {props.children}
    </Flex>
  );
};

export const AvailabilityLayout = (props: { children: React.ReactNode }): JSX.Element => {
  const theme = useTheme();
  return (
    <Flex
      {...sectionStyle}
      alignItems="start"
      width={['100%', '50%', '100%']}
      mt={['0x', '16x', '0x']}
      borderBottom="1px solid"
      borderBottomColor={theme.colors.separator.primary}
      pb="8x"
      pt="6x"
      sx={{
        scrollMarginTop: SCROLL_MARGIN_WITHOUT_SEPARATOR,
      }}
      data-testid="sitter-availability"
    >
      {props.children}
    </Flex>
  );
};

export const LocationLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      {...sectionStyle}
      marginRight={['0x', '8x', '0x']}
      sx={{ marginY: '40px' }}
      width={['100%', '50%', '100%']}
      flexDirection="column"
      alignItems="start"
      alignSelf="stretch"
    >
      {props.children}
    </Flex>
  );
};

export const PetsLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      {...sectionStyle}
      marginY="8x"
      flexDirection="column"
      data-testid="sitter-pet-list"
      alignItems="start"
      marginLeft={['0x', '8x', '0x']}
      width={['100%', '50%', '100%']}
    >
      {props.children}
    </Flex>
  );
};

export const AboutLayout = (props: { children: React.ReactNode }): JSX.Element => {
  const theme = useTheme();
  return (
    <Box borderBottom={`1px solid ${theme.colors.separator.primary}`} pb="8x" mb="10x">
      <Box pb="2x">{props.children}</Box>
    </Box>
  );
};

export const ReviewsLayout = (props: { children: React.ReactNode }): JSX.Element => {
  const theme = useTheme();
  return (
    <Box borderBottom={`1px solid ${theme.colors.separator.primary}`} pb="10x" mb="10x">
      <Flex id="reviews" sx={{ scrollMarginTop: SCROLL_MARGIN_WITH_SEPARATOR }}>
        {props.children}
      </Flex>
    </Box>
  );
};

export const SkillsAndRestTableLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      display={['flex', 'flex', 'none']}
      id="details"
      sx={{ scrollMarginTop: SCROLL_MARGIN_WITH_SEPARATOR }}
    >
      {props.children}
    </Flex>
  );
};

export const SkillsAndRestDesktopLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return <Flex display={['none', 'none', 'flex']}>{props.children}</Flex>;
};

export const MoreSittersLayout = (props: { children: React.ReactNode }): JSX.Element => {
  return (
    <Flex
      background="secondary"
      paddingX="4x"
      paddingY="12x"
      flexDirection="column"
      alignItems="center"
      alignSelf="stretch"
    >
      <Flex
        maxWidth={[MOBILE_MAX_WIDTH_LAYOUT, 'initial']}
        flexDirection="column"
        alignItems="center"
        width="100%"
      >
        {props.children}
      </Flex>
    </Flex>
  );
};
